<template>
  <div :class="filterClassname">
    <Dropdown
      v-model:shown="isDropdownVisible"
      class="fw-filterpel-filterentry-wrapper"
      popper-class="fw-filterpel-dropdown"
    >
      <button :class="[`fw-filterpel-filterentry-button`, { active: localValue !== undefined }]">
        {{ filter.label }}
      </button>
      <template #popper>
        <div class="fw-filterpel-filterentry-dropdown">
          <component
            :is="filterComponent(filter.type)"
            v-model="localValue"
            v-bind="filterProps"
            :filter="filter"
            @apply-filter="apply"
            @clear-filter="clear"
            @close-dropdown="close"
          />
          <button
            class="fw-filterpel-filterentry-dropdown-close"
            :aria-label="languageStrings.close[languageStore.language]"
            @click="close"
          />
        </div>
      </template>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import slugify from '@sindresorhus/slugify';
import { Dropdown } from "floating-vue";

import FilterText from "@components/filters/FilterText.vue";
import FilterNumber from "@components/filters/FilterNumber.vue";
import FilterMulti from "@components/filters/FilterMulti.vue";

import useLanguageStore from "@/stores/language";

const props = defineProps<{
  modelValue: unknown,
  filter: IFilter,
  filterProps?: Record<string, unknown>,
}>();
const emit = defineEmits(["apply", "hide", "update:modelValue"]);
const localValue = ref(props.modelValue);

// define stores
const languageStore = useLanguageStore();

// define language strings
const languageStrings: ILanguageStrings = {
  close: {
    de: "Schließen",
    en: "Close"
  }
};

// Watch for changes in the prop and update the local value
watch(() => props.modelValue, (newValue) => {
  localValue.value = newValue;
});

// Emit the updated value when localValue changes
watch(localValue, (newValue) => {
  emit('update:modelValue', newValue);
});

const isDropdownVisible = ref(false);

// dynamic filter component
const filterComponent = (type: string) => {
  switch (type) {
    case "text": {
      return FilterText;
    }
    case "number": {
      return FilterNumber;
    }
    case "multi": {
      return FilterMulti;
    }
    default: {
      return undefined;
    }
  }
};

const filterClassname = computed(() => {
  return [
    `fw-filterpel-filterentry`,
    `filter-${slugify(props.filter?.key)}`
  ];
});

const apply = (value: unknown) => {
  isDropdownVisible.value = false;
  emit('update:modelValue', value);
  emit('apply');
};

const clear = () => {
  emit('update:modelValue', undefined);
};

const close = () => {
  isDropdownVisible.value = false;
};

</script>
